import React, { useState, useEffect } from 'react';
import { recallService } from '../../services/recallService';
import { engineerService } from '../../services/engineerService';
import { uploadRecallPhoto, isDataUrl } from '../../services/photoService';

export default function MobileRecall({ executions, engineers: propsEngineers, onActionSave, loading: propsLoading }) {
  const [view, setView] = useState('LIST');
  const [selectedExec, setSelectedExec] = useState(null);
  const [targetVehicle, setTargetVehicle] = useState(null);
  const [localEngineers, setLocalEngineers] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);

  // --- [신규 내장] 모바일 검색용 쿼리 필터 및 페이지 보존 상태계 ---
  const [vehSearch, setVehSearch] = useState('');
  const [vehPage, setVehPage] = useState(1);

  const [actionDesc, setActionDesc] = useState('');
  const [photos, setPhotos] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState('');
  
  const getCurrentDateTime = () => {
    const tzOffset = new Date().getTimezoneOffset() * 60000; 
    return new Date(Date.now() - tzOffset).toISOString().slice(0, 16);
  };
  const [actionDateTime, setActionDateTime] = useState(getCurrentDateTime());

  useEffect(() => {
    // 🚀 탭 고속 전환 및 비동기 응답 지연에 따른 트래픽 중복 폭발을 통제하는 보호막 플래그 선언
    let isMounted = true;

    const fetchEngineers = async () => {
      try {
        const data = await engineerService.getEngineers();
        if (isMounted) {
          setLocalEngineers(data || []);
        }
      } catch (err) { console.error(err); }
    };
    fetchEngineers();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (executions && selectedExec) {
      const updated = executions.find(e => e.id === selectedExec.id);
      if (updated) setSelectedExec(updated);
    }
  }, [executions, selectedExec]);

  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader(); reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image(); img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800; const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH; canvas.height = img.height * scaleSize;
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
      };
    });
  };

  const handleMobilePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) return alert("사진은 최대 5장까지만 등록 가능합니다.");
    setLocalLoading(true);
    const compressedPhotos = [];
    for (const file of files) {
      const resized = await resizeImage(file); compressedPhotos.push(resized);
    }
    setPhotos(prev => [...prev, ...compressedPhotos]);
    setLocalLoading(false);
  };

  const handleOpenActionForm = (vehicle) => {
    setTargetVehicle(vehicle);
    setActionDesc(vehicle.actionData?.actionDesc || '');
    setPhotos(vehicle.actionData?.photos || []);
    setSelectedEngineer(vehicle.actionData?.engineer || '');
    setActionDateTime(vehicle.actionData?.actionDateTime || getCurrentDateTime());
    setView('FORM');
  };

  const submitMobileAction = async () => {
    if (!actionDesc) return alert("조치 내역을 입력해주세요.");
    if (!selectedEngineer) return alert("담당 엔지니어를 선택해주세요.");

    setLocalLoading(true);
    try {
      // 새로 고른 사진만 파일 보관소(Storage)에 올리고, 문서에는 짧은 주소만 남긴다.
      // 사진을 문서에 직접 넣으면 1MB 한도를 넘겨 저장이 통째로 실패한다.
      const uploaded = [];
      for (const src of photos) {
        uploaded.push(isDataUrl(src) ? await uploadRecallPhoto(selectedExec.id, targetVehicle.carNumber, src) : src);
      }

      const actionPayload = { actionDesc, photos: uploaded, engineer: selectedEngineer, actionDateTime };
      await onActionSave(selectedExec.id, targetVehicle.carNumber, actionPayload);
      setView('DETAIL');
    } catch (err) {
      console.error('리콜 조치 저장 실패:', err);
      // 업로드가 실패했는데 저장했다고 알리면 사진이 사라진 줄 모르게 되므로 분명히 알린다.
      alert("사진 업로드에 실패하여 저장하지 않았습니다.\n네트워크를 확인하고 다시 시도해 주세요.");
    }
    finally { setLocalLoading(false); }
  };

  // 🚀 [모바일 데이터 필터] 실시간 검색 분기 및 10대 배열 분할 연동
  const filteredTargets = (selectedExec?.targets || []).filter(t => 
    (t.carNumber || '').toLowerCase().includes(vehSearch.toLowerCase())
  );
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredTargets.length / itemsPerPage);
  const paginatedTargets = filteredTargets.slice((vehPage - 1) * itemsPerPage, vehPage * itemsPerPage);

  const isLoading = propsLoading || localLoading;

  return (
    <div style={{ padding: '16px', width: '100%', boxSizing: 'border-box', backgroundColor: '#f8fafc' }}>
      
      {isLoading && <div style={{ textAlign: 'center', color: '#0284c7', padding: '15px', fontSize: '13px', fontWeight: 'bold' }}>🔄 데이터 전송 및 동기화 중...</div>}

      {/* 1. 리콜 캠페인 전체 진행 현황 (LIST) */}
      {view === 'LIST' && !isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
          <div style={{ fontSize: '15px', fontWeight: '900', color: '#1e293b', marginBottom: '2px' }}>📋 리콜 캠페인 진행 현황</div>
          {executions && executions.map(e => {
            const total = e.targets?.length || 0;
            const done = e.targets?.filter(t => t.status === '완료').length || 0;
            const pct = total === 0 ? 0 : Math.round((done / total) * 100);
            return (
              <div key={e.id} onClick={() => { setSelectedExec(e); setVehSearch(''); setVehPage(1); setView('DETAIL'); }} style={{ background: '#ffffff', padding: '18px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', cursor: 'pointer', width: '100%', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748b', marginBottom: '8px' }}><span>📅 {e.regDate || '날짜없음'}</span><span style={{ fontWeight: '800', color: pct === 100 ? '#10b981' : '#ea580c', background: pct === 100 ? '#e0fdf4' : '#fff7ed', padding: '3px 8px', borderRadius: '6px' }}>{pct}% 완료</span></div>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>{e.title}</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #f1f5f9', fontSize: '13px' }}><span style={{ color: '#475569', background: '#f1f5f9', padding: '4px 10px', borderRadius: '8px', fontWeight: '600' }}>🚗 {e.modelName}</span><span style={{ color: '#64748b', fontWeight: '700' }}>이행률: <b>{done}</b> / {total} 대</span></div>
              </div>
            );
          })}
        </div>
      )}

      {/* 2. 캠페인 대상 차량 상세 리스트 뷰 (DETAIL - 검색 및 페이징 실장) */}
      {view === 'DETAIL' && selectedExec && (
        <div style={{ width: '100%' }}>
          <button onClick={() => setView('LIST')} style={{ width: '100%', background: '#1e293b', color: '#ffffff', border: 'none', padding: '12px', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold', marginBottom: '14px', cursor: 'pointer' }}>◀ 전체 리콜 캠페인 목록 이동</button>
          
          <div style={{ background: '#ffffff', padding: '16px', borderRadius: '14px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>지휘 타깃 차종: {selectedExec.modelName}</span>
            <h3 style={{ fontSize: '16px', fontWeight: '900', margin: 0, color: '#1e293b' }}>📢 {selectedExec.title}</h3>
          </div>

          {/* 🚀 [모바일 신규 탑재] 실시간 차량 검색 인풋바 */}
          <div style={{ marginBottom: '14px' }}>
            <input 
              type="text" 
              placeholder="🔍 찾으시는 차량번호 검색..." 
              value={vehSearch}
              onChange={e => { setVehSearch(e.target.value); setVehPage(1); }}
              style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '12px', boxSizing: 'border-box', fontSize: '14px', outline: 'none' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
            {paginatedTargets.map((t) => (
              <div key={t.carNumber} style={{ background: '#ffffff', padding: '16px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.01)', width: '100%', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontWeight: '900', fontSize: '16px', color: '#1e293b' }}>{t.carNumber}</span>
                  <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '800', background: t.status === '완료' ? '#f0fdf4' : '#fff7ed', color: t.status === '완료' ? '#16a34a' : '#ea580c' }}>{t.status}</span>
                </div>
                <div style={{ fontSize: '13px', color: '#475569', marginBottom: '12px' }}>
                  👤 고객명: <strong style={{color: '#1e293b'}}>{t.customerName}</strong>
                  <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>📍 관제주소: {t.address}</div>
                </div>
                {t.status === '완료' && t.doneDate && (
                  <div style={{ fontSize: '12px', color: '#475569', background: '#f8fafc', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', border: '1px solid #f1f5f9' }}>📅 이행일: {t.doneDate} / 정비사: {t.actionData?.engineer || '기록없음'}</div>
                )}
                <button onClick={() => handleOpenActionForm(t)} style={{ width: '100%', padding: '11px', background: t.status === '완료' ? '#f1f5f9' : '#ef4444', color: t.status === '완료' ? '#475569' : '#ffffff', border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>
                  {t.status === '완료' ? '🛠️ 기입된 조치 기록 수정·조회하기' : '🔧 현장 즉시 조치 이행하기'}
                </button>
              </div>
            ))}
          </div>

          {/* 🚀 [모바일 신규 탑재] 10대 표출 범위 제어 모바일 전용 슬림 내비게이터 */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '10px', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <button disabled={vehPage === 1} onClick={() => setVehPage(p => p - 1)} style={{ padding: '8px 16px', background: vehPage === 1 ? '#e2e8f0' : '#1e293b', color: vehPage === 1 ? '#94a3b8' : '#ffffff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: vehPage === 1 ? 'not-allowed' : 'pointer' }}>이전</button>
              <span style={{ fontSize: '13px', fontWeight: '800', color: '#334155' }}>{vehPage} / {totalPages} 페이지</span>
              <button disabled={vehPage === totalPages} onClick={() => setVehPage(p => p + 1)} style={{ padding: '8px 16px', background: vehPage === totalPages ? '#e2e8f0' : '#1e293b', color: vehPage === totalPages ? '#94a3b8' : '#ffffff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: vehPage === totalPages ? 'not-allowed' : 'pointer' }}>다음</button>
            </div>
          )}
        </div>
      )}

      {/* 3. 현장 조치 내역 입력 폼 뷰 (FORM) */}
      {view === 'FORM' && targetVehicle && (
        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '18px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', width: '100%', boxSizing: 'border-box' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '900', color: '#1e293b', borderBottom: '2px solid #ef4444', paddingBottom: '10px' }}>🔧 리콜 조치 기록 등록</h3>
          <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '18px' }}>
            <div style={{ fontSize: '18px', fontWeight: '900', color: '#1e293b' }}>{targetVehicle.carNumber}</div>
            <div style={{ fontSize: '13px', marginTop: '4px', color: '#64748b' }}>대상 고객: {targetVehicle.customerName}</div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569', display: 'block', marginBottom: '6px' }}>⚙️ 현장 기술 조치 내용</label>
            <textarea style={{ width: '100%', height: '110px', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '10px', boxSizing: 'border-box', outline: 'none', fontSize: '13px', lineHeight: '1.5', fontFamily: 'inherit' }} placeholder="수행한 리콜 정비 지침 조치 내역을 상세히 기술하세요." value={actionDesc} onChange={e => setActionDesc(e.target.value)} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569', display: 'block', marginBottom: '6px' }}>📸 현장 정비 사진 증빙 ({photos.length}/5)</label>
            <input type="file" accept="image/*" multiple onChange={handleMobilePhotoUpload} style={{ width: '100%', boxSizing: 'border-box', fontSize: '12px', color: '#64748b' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginTop: '12px' }}>
              {photos.map((src, i) => (
                <div key={i} style={{ height: '60px', background: `url(${src}) center/cover`, borderRadius: '8px', position: 'relative', border: '1px solid #cbd5e1' }}><span onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: 'white', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>x</span></div>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569', display: 'block', marginBottom: '6px' }}>👨‍🔧 공정 담당 엔지니어</label>
            <select value={selectedEngineer} onChange={e => setSelectedEngineer(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '10px', boxSizing: 'border-box', fontSize: '14px', fontWeight: '600', color: '#1e293b', background: '#ffffff' }}>
              <option value="">-- 엔지니어 선택 --</option>
              {(propsEngineers || localEngineers).map(eng => <option key={eng.id} value={eng.name}>{eng.name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#475569', display: 'block', marginBottom: '6px' }}>⏱️ 작업 완료 일시</label>
            <input type="datetime-local" value={actionDateTime} onChange={e => setActionDateTime(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '10px', boxSizing: 'border-box', outline: 'none', fontSize: '14px', fontWeight: '500' }} />
          </div>
          <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
            <button onClick={submitMobileAction} style={{ flex: 1, padding: '14px', background: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '12px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)' }}>💾 조치 결과 저장</button>
            <button onClick={() => setView('DETAIL')} style={{ padding: '14px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '12px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer' }}>취소</button>
          </div>
        </div>
      )}

    </div>
  );
}